import React, { useEffect, useState } from "react";

import { associationsService } from "api/services/associations.service";
import { sportsService, Sport } from "api/services/sports.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import { extractList, getApiErrorMessage } from "utils/api-error";

import type { Association, AssociationCreateRequest } from "api/types/associations.types";
import "pages/AdminPage.scss";
import "./AssociationList.scss";

const AssociationList: React.FC = () => {
  const [items, setItems] = useState<Association[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<AssociationCreateRequest>({
    name: "",
    sport: 0,
    full_name: "",
    is_active: true,
    order: 0,
  });

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await associationsService.list();
      setItems(extractList<Association>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchSports = async () => {
    try {
      const res = await sportsService.list();
      const data = res.data;
      setSports(Array.isArray(data) ? data : (data.sports ?? []));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchList();
    fetchSports();
  }, []);

  const filteredItems = items.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return item.name.toLowerCase().includes(q) || item.full_name.toLowerCase().includes(q) || item.sport_name.toLowerCase().includes(q);
  });

  /* ───────── 모달 열기/닫기 ───────── */
  const openCreateModal = () => {
    setEditingId(null);
    setForm({ name: "", sport: 0, full_name: "", is_active: true, order: 0 });
    setShowModal(true);
  };

  const openEditModal = (item: Association) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      sport: item.sport,
      full_name: item.full_name,
      is_active: item.is_active,
      order: item.order,
    });
    setShowModal(true);
  };

  /* ───────── 추가 / 수정 ───────── */
  const handleSave = async () => {
    if (!form.name || !form.sport) return;
    setSaving(true);
    try {
      if (editingId) {
        await associationsService.update(editingId, form);
      } else {
        await associationsService.create(form);
      }
      setShowModal(false);
      fetchList();
    } catch (err) {
      alert(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<Association>[] = [
    { key: "id", title: "ID", width: "60px" },
    { key: "name", title: "협회명" },
    { key: "full_name", title: "정식 명칭" },
    { key: "sport_name", title: "종목" },
    {
      key: "is_active",
      title: "상태",
      // width: "80px",
      render: (v) => (v ? <Badge variant="active">활성</Badge> : <Badge variant="draft">비활성</Badge>),
    },
    // { key: "order", title: "순서", width: "60px" },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="협회 관리"
        description="자격증 발급 협회 목록을 관리합니다."
        actions={[
          <Button key="add" variant="primary" onClick={openCreateModal}>
            + 협회 추가
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input placeholder="협회명, 종목 검색" value={search} onChange={(e) => setSearch(e.target.value)} prefix="🔍" />
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : <Table columns={columns} data={filteredItems} rowKey="id" onRowClick={openEditModal} />}
      </div>

      <Modal
        open={showModal}
        title={editingId ? "협회 수정" : "협회 추가"}
        onClose={() => setShowModal(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving || !form.name || !form.sport}>
              {saving ? "저장 중..." : editingId ? "수정" : "추가"}
            </Button>
          </>
        }
      >
        <div className="association-form">
          <Input label="협회명 *" placeholder="예: AIDA, PADI" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <Input label="정식 명칭" placeholder="예: Association Internationale pour le Développement de l'Apnée" value={form.full_name ?? ""} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} />
          <Select label="종목 *" value={form.sport ? String(form.sport) : ""} onChange={(e) => setForm((p) => ({ ...p, sport: Number(e.target.value) }))} options={[{ value: "", label: "종목을 선택하세요" }, ...sports.map((s) => ({ value: String(s.id), label: s.name }))]} />
          <Select
            label="상태"
            value={form.is_active ? "true" : "false"}
            onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.value === "true" }))}
            options={[
              { value: "true", label: "활성" },
              { value: "false", label: "비활성" },
            ]}
          />
          <Input label="정렬 순서" type="number" placeholder="0" value={String(form.order ?? 0)} onChange={(e) => setForm((p) => ({ ...p, order: Number(e.target.value) }))} />
        </div>
      </Modal>
    </div>
  );
};

export default AssociationList;
